import { type Tranche } from "@lib/tranche";
import Matrix from "algorithm/matrix";

/**
 * Convert an incremental tranche list to a cumulative tranche list.
 */
export function trancheListToAlgoTranches(
  tranches: Tranche[],
  evaluableIndicesMap: { [evaluableId: string]: number },
  evaluableMaxAllocations: { [evaluableId: string]: number | null },
  evaluableIdWhitelist: Set<string>,
): Matrix<"tranche", "evaluable+hold"> {
  const numEvaluables = Object.keys(evaluableIndicesMap).length;
  const result = Matrix.zeros<"tranche", "evaluable+hold">(
    tranches.length,
    numEvaluables,
  );
  if (tranches.length === 0 || evaluableIdWhitelist.size === 0) {
    return result;
  }

  // Build compact active-column metadata once so the row loop is dense.
  const activeColumns: number[] = [];
  const activeMaxAllocations: number[] = [];
  const activeIndexByEvaluableId: { [evaluableId: string]: number } = {};

  for (const evaluableId of evaluableIdWhitelist) {
    const col = evaluableIndicesMap[evaluableId];
    if (col == null) {
      continue;
    }
    activeIndexByEvaluableId[evaluableId] = activeColumns.length;
    activeColumns.push(col);
    activeMaxAllocations.push(evaluableMaxAllocations[evaluableId] ?? Infinity);
  }

  if (activeColumns.length === 0) {
    return result;
  }

  const runningAllocations = new Float64Array(activeColumns.length);
  const data = result.data;

  for (let row = 0; row < tranches.length; row++) {
    const tranche = tranches[row];
    if (tranche != null) {
      for (const evaluableId in tranche.allocations) {
        const activeIndex = activeIndexByEvaluableId[evaluableId];
        if (activeIndex == null) {
          continue;
        }

        const alloc = tranche.allocations[evaluableId];
        if (alloc == null || alloc.amount === 0) {
          continue;
        }

        const maxAlloc = activeMaxAllocations[activeIndex] ?? Infinity;
        const current = runningAllocations[activeIndex] ?? 0;
        const next = current + alloc.amount;
        runningAllocations[activeIndex] = next > maxAlloc ? maxAlloc : next;
      }
    }

    const rowOffset = row * numEvaluables;
    for (let i = 0; i < activeColumns.length; i++) {
      const col = activeColumns[i];
      if (col == null) {
        continue;
      }
      data[rowOffset + col] = runningAllocations[i] ?? 0;
    }
  }

  return result;
}

/** Preserve each preference segment until a cap is reached. Clamping only
 * endpoints changes the slope before the cap and underfunds capped grants. */
export function capPreferenceList(
  preferences: Matrix<"tranche", "evaluable+hold">,
  caps: readonly number[],
): Matrix<"tranche", "evaluable+hold"> {
  if (preferences.n_rows === 0) {
    return preferences;
  }
  const rows: number[][] = [];
  let previous = Array<number>(preferences.n_cols).fill(0);
  for (const next of preferences.toArrays()) {
    const breaks = new Set([1]);
    for (let p = 0; p < next.length; p++) {
      const cap = caps[p] ?? Infinity;
      if (previous[p]! < cap && cap < next[p]!) {
        breaks.add((cap - previous[p]!) / (next[p]! - previous[p]!));
      }
    }
    for (const fraction of [...breaks].sort((a, b) => a - b)) {
      rows.push(
        next.map((amount, p) =>
          Math.min(
            previous[p]! + fraction * (amount - previous[p]!),
            caps[p] ?? Infinity,
          ),
        ),
      );
    }
    previous = next;
  }
  return new Matrix(rows);
}
